/**
 * Shared helpers for the bulk exports (diary and Kinderakte): German
 * date formatting, filename building and normalization of Parse
 * objects into plain data the generators consume.
 */

import {
	APP_ADRESS_TITEL, APP_ADRESS_TITEL_ZUSATZ, APP_ADRESS_STRASSE,
	APP_ADRESS_STADT, APP_TELEFON, APP_FAX, APP_MAIL, APP_WEB,
	APP_LETTERHEAD_LOGO,
} from "../../configuration/AppConfigurations";

var config = require("../../../client_config.json");

const DATE_OPTIONS = { year: "numeric", month: "2-digit", day: "2-digit" };

// The address lines come from the per-container runtime config, not a
// baked-in customer table: whichever facility this container serves
// supplies its own via ADRESS_* / TELEFON / ... in its compose stack.
const LETTERHEAD_ADDRESS = {
	adress_titel: APP_ADRESS_TITEL,
	adress_titel_zusatz: APP_ADRESS_TITEL_ZUSATZ,
	adress_strasse: APP_ADRESS_STRASSE,
	adress_stadt: APP_ADRESS_STADT,
	telefon: APP_TELEFON,
	fax: APP_FAX,
	mail: APP_MAIL,
	web: APP_WEB,
};

// The bundled letterhead logos, keyed by the runtime LETTERHEAD_LOGO
// value. Only a customer that names one here gets a PDF logo; every
// other customer prints none, never a foreign logo. Each blob is
// letterboxed to the PDF logo box aspect, so it draws undistorted.
const LETTERHEAD_LOGO = {
	kjhz: config.kjhz_logo,
	"kjd-belleben": config.kjd_logo,
	garsena: config.garsena_logo,
	fuchsbau: config.fuchsbau_logo,
	demo: config.demo_logo,
};

/**
 * Whether this container has a configured letterhead. Without one the
 * exports deliberately print no address and no logo, so an
 * unconfigured customer never inherits another customer's branding.
 */
export function hasLetterhead() {
	return Boolean(APP_ADRESS_TITEL);
}

/** A letterhead value (address line, or "logo") by short key. */
export function clientConfig(key) {
	if (key === "logo") {
		if (!hasLetterhead()) { return undefined; }
		const logo = LETTERHEAD_LOGO[APP_LETTERHEAD_LOGO];
		// Trimmed: stray whitespace around a data URI (the legacy kjd
		// blob carried one) breaks jsPDF's image type detection.
		return logo ? logo.trim() : undefined;
	}
	return LETTERHEAD_ADDRESS[key] || "";
}

export function startOfDay(date) {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d;
}

export function startOfNextDay(date) {
	const d = startOfDay(date);
	d.setDate(d.getDate() + 1);
	return d;
}

export function formatDate(date) {
	return date ? date.toLocaleDateString("de-DE", DATE_OPTIONS) : undefined;
}

export function formatDateTime(date) {
	if (!date) { return undefined; }
	return formatDate(date) + " - " + date.toLocaleTimeString("de-DE", {
		hour: "2-digit",
		minute: "2-digit",
	});
}

/** Local date as sortable YYYY-MM-DD (for filenames). */
export function isoDate(date) {
	if (!date) { return undefined; }
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return date.getFullYear() + "-" + month + "-" + day;
}

export function exportFilename(client, from, to, extension,
                               prefix = "Tagebuch") {
	const lastName = client.attributes.last_name || "Klient";
	const fromPart = from ? isoDate(from) : "Beginn";
	const toPart = to ? isoDate(to) : isoDate(new Date());
	return prefix + "_" + lastName + "_" + fromPart + "_" + toPart +
		"." + extension;
}

/** Range label like "01.02.2026 - 31.07.2026" ("Beginn" = no bound). */
export function formatRange(from, to) {
	return (from ? formatDate(from) : "Beginn") + " - " +
		(to ? formatDate(to) : formatDate(new Date()));
}

/** Client head-sheet data as a plain object. */
export function describeClient(client) {
	const attrs = client.attributes;
	const group = attrs.group;
	return {
		name: (attrs.last_name || "") + ", " + (attrs.first_name || ""),
		groupName: group ? group.attributes.group_name : undefined,
		birthday: formatDate(attrs.birth_date),
		helpBegin: formatDate(attrs.entry),
	};
}

// Everything outside printable ASCII, Latin-1, and the Unicode
// characters mapped into the WinAnsi 0x80-0x9f slots (dashes, curly
// quotes, Euro, ...).
const NOT_WIN_ANSI = new RegExp(
	"[^\\u0020-\\u007e\\u00a0-\\u00ff\\u0152\\u0153\\u0160\\u0161" +
	"\\u0178\\u017d\\u017e\\u0192\\u02c6\\u02dc\\u2013\\u2014" +
	"\\u2018\\u2019\\u201a\\u201c\\u201d\\u201e\\u2020\\u2021" +
	"\\u2022\\u2026\\u2030\\u2039\\u203a\\u20ac\\u2122]", "g");

/**
 * Make a string encodable as WinAnsi (the encoding of the standard
 * PDF fonts): normalize to NFC first -- macOS filenames arrive in NFD,
 * where "ü" is "u" + combining diaeresis and unencodable -- then
 * replace every character outside the WinAnsi set with "?".
 */
export function winAnsiSafe(text) {
	return String(text).normalize("NFC").replace(NOT_WIN_ANSI, "?");
}

/** Trigger a browser download of a Blob. */
export function downloadBlob(filename, blob) {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	URL.revokeObjectURL(url);
}

/**
 * Attachment summary like "2 Bilder, 1 Dokument (Bericht.pdf)", or
 * undefined when the entry has no attachments. Pictures live directly
 * on the entry (`pictures`, Array of File); other files are EntryFile
 * pointers (`otherFiles`) carrying the original filename.
 */
export function formatAttachments(entry) {
	const pictures = entry.attributes.pictures || [];
	const documents = entry.attributes.otherFiles || [];
	const parts = [];
	if (pictures.length > 0) {
		parts.push(pictures.length === 1
			? "1 Bild" : pictures.length + " Bilder");
	}
	if (documents.length > 0) {
		// Guard against unfetched pointers; titles may be missing on
		// legacy rows, then only the count is shown.
		// macOS filenames arrive in NFD ("u" + combining diaeresis);
		// recompose so umlauts stay one WinAnsi-encodable character.
		const names = documents
			.map((file) => file.attributes && file.attributes.fileTitle)
			.filter(Boolean)
			.map((name) => String(name).normalize("NFC"));
		let part = documents.length === 1
			? "1 Dokument" : documents.length + " Dokumente";
		if (names.length > 0) { part += " (" + names.join(", ") + ")"; }
		parts.push(part);
	}
	return parts.length > 0 ? parts.join(", ") : undefined;
}

/** Entry data (from an EntryLink) as a plain object. */
export function describeEntryLink(entryLink) {
	const entry = entryLink.attributes.entry;
	const author = entry.attributes.author;
	const person = author ? author.attributes.dependency : undefined;
	const type = entry.attributes.type;
	return {
		title: entry.attributes.title || "",
		authorName: person
			? person.attributes.first_name + " " + person.attributes.last_name
			: undefined,
		createdAt: entry.createdAt,
		counter: entry.attributes.entryCounter,
		typeName: type ? type.attributes.name : undefined,
		tags: (entry.attributes.attributes || [])
			.map((attribute) => attribute.attributes.name),
		rating: entryLink.attributes.rating,
		isPrivate: entry.attributes.isPrivate === true ||
			entryLink.attributes.isPrivate === true,
		attachments: formatAttachments(entry),
		pictures: (entry.attributes.pictures || [])
			.map((file) => ({ name: file._name, url: file._url }))
			.filter((picture) => picture.url),
		documents: (entry.attributes.otherFiles || [])
			.filter((file) => file.attributes && file.attributes.file)
			.map((file) => ({
				title: file.attributes.fileTitle || "Dokument",
				url: file.attributes.file._url,
			})),
		html: entry.attributes.text || "",
	};
}

/** Kinderakte document (ClientFile) data as a plain object. */
export function describeClientFile(clientFile) {
	const attrs = clientFile.attributes;
	return {
		title: attrs.title || attrs.pdfTitle || "Dokument",
		category: attrs.category
			? attrs.category.attributes.name : undefined,
		// Legacy rows may hold a pointer here instead of the name.
		creator: typeof attrs.creator === "string"
			? attrs.creator : undefined,
		documentType: attrs.documentType,
		date: formatDate(attrs.date || clientFile.createdAt),
		fileName: attrs.pdfTitle,
		url: attrs.pdf ? attrs.pdf._url : undefined,
	};
}

/**
 * Filter and sort the Kinderakte documents for the export.
 *
 * options:
 *   from        : Date lower bound (inclusive, start of day) or
 *                 undefined for no lower bound
 *   to          : Date upper bound (inclusive, whole day) or undefined
 *   categoryIds : ids of the selected categories ([] = no filter;
 *                 documents without a category only pass an empty
 *                 filter)
 *   sortOrder   : "asc" (oldest first) or "desc" (newest first)
 *
 * Bounds apply to createdAt -- the same field the module's list
 * filter uses.
 */
export function filterClientFilesForExport(clientFiles, options) {
	const { from, to, categoryIds, sortOrder } = options;
	const lower = from ? startOfDay(from) : undefined;
	const upper = to ? startOfNextDay(to) : undefined;
	const filtered = clientFiles.filter((clientFile) => {
		if (lower && clientFile.createdAt < lower) { return false; }
		if (upper && clientFile.createdAt >= upper) { return false; }
		if (categoryIds && categoryIds.length > 0) {
			const category = clientFile.attributes.category;
			if (!category) { return false; }
			return categoryIds.indexOf(category.id) > -1;
		}
		return true;
	});
	return filtered.sort((a, b) => sortOrder === "desc"
		? b.createdAt - a.createdAt
		: a.createdAt - b.createdAt);
}
